package com.swetdot.swetgram.ui.groups

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.groups.GroupRepository
import com.swetdot.swetgram.data.groups.asGroupFailure
import com.swetdot.swetgram.domain.Group
import com.swetdot.swetgram.domain.GroupFailure
import com.swetdot.swetgram.domain.GroupId
import com.swetdot.swetgram.domain.GroupMember
import com.swetdot.swetgram.domain.GroupMemberRemoval
import com.swetdot.swetgram.domain.GroupRole
import com.swetdot.swetgram.domain.GroupRoleAssignment
import com.swetdot.swetgram.domain.UserId
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/**
 * SPS §12.6's Members tab: the member list, who the admins are, a search, and removal.
 *
 * "Leave group" is §12.6's fifth item and it is deliberately NOT here -- it lives on the group
 * screen next to Delete, because the two are one decision ("how do I get out of this group?") and
 * only one of them is ever available to a given person. See `groupExitOption`.
 *
 * ROLES ARE NOW EDITABLE, in the place pass 2 left for them. §12.7's "Promote admin" row is owner
 * only, and demotion is the same operation with the other body value -- so there is ONE control per
 * row, not two, and `Group.nextRoleFor` says which direction it points.
 *
 * BANNING IS HERE TOO, and it is not the same act as removing. A ban removes the person AND stops
 * them coming back through a link or a request, so the two are separate actions with separate
 * confirmations rather than one action with a checkbox. `removeGroupMember?action=remove_and_ban`
 * could have done both in one call and is deliberately not used -- see `GroupMemberRemoval`.
 *
 * WHO MAY DO WHAT TO WHOM is not decided in this file. `domain/Group.kt` holds every one of those
 * rules -- `mayRemove`, `mayBan` (the same rule, delegated) and `mayChangeRole` -- and a second copy
 * here is how a screen and a domain start disagreeing about a moderation permission.
 *
 * EVERY ONE OF THESE ACTIONS IS RE-CHECKED AT THE MOMENT IT IS TAKEN, against the group as it was
 * last read, not against the button that was drawn. Roles change while a card is open: the person
 * being demoted may have been made owner a second ago, and the person doing it may have been
 * demoted themselves. The guard costs nothing and it is the only one that does not depend on what
 * the screen believed a moment ago.
 *
 * NONE OF IT IS OPTIMISTIC. Moderation on a shared surface has to wait for the server, because
 * showing a member gone -- or an admin promoted -- before the write landed lets two admins each
 * believe they did something to a person who is still exactly where they were.
 */

// ---------------------------------------------------------------------------------------------
// Search -- pure, so it can be tested without a repository
// ---------------------------------------------------------------------------------------------

/**
 * SPS §12.6's member search, over the display name and the username.
 *
 * A LOCAL filter, and it has to be: the frozen contract gives `listGroupMembers` exactly two
 * parameters, `cursor` and `limit`, and no query. That is a place the reference folder and the
 * contract disagree -- §12.6 asks for member search outright -- and the contract wins, so the client
 * does the filtering. Filtering only the pages the client happens to be holding would be a search
 * that silently misses people, so [GroupMembersViewModel.onQueryChange] pulls the remaining pages
 * first. That is bounded work: `Group.member_count` is `maximum: 500` in the contract.
 *
 * Matches anywhere in the string rather than only at the start: a member list is scanned for a name
 * somebody half-remembers, and "smith" failing to find "Ada Smith" is what makes a search box look
 * broken. A leading `@` is dropped, since the row shows one and people type back what they read.
 */
internal fun matchesMemberQuery(member: GroupMember, query: String): Boolean {
    val needle = query.trim().removePrefix("@").lowercase()
    if (needle.isEmpty()) return true
    return member.user.displayName.lowercase().contains(needle) ||
        member.user.username.lowercase().contains(needle)
}

/** Order is preserved: the server sorts by `(joined_at, user_id)`, which puts the owner first. */
internal fun filterMembers(members: List<GroupMember>, query: String): List<GroupMember> =
    if (query.isBlank()) members else members.filter { matchesMemberQuery(it, query) }

/**
 * What to say when a search is running against a list that is not all here yet, or null when the
 * results are complete and need no caveat.
 *
 * Two different sentences, because only one of the two situations resolves on its own. While the
 * sweep is running the answer is about to become complete; once it has stopped -- which only happens
 * when a page failed, and the banner above already says so -- what is on screen is all the user will
 * get until they retry. Saying "still loading" in the second case leaves somebody waiting for pages
 * that are not coming.
 */
// TODO(localization): these strings move into resources for EN + RU.
internal fun searchScopeNote(searchIncomplete: Boolean, loadingMore: Boolean): String? = when {
    !searchIncomplete -> null
    loadingMore -> "Still loading the rest of the member list…"
    else -> "Some members have not loaded, so these results are incomplete."
}

// ---------------------------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------------------------

data class GroupMembersUiState(
    /**
     * The group itself, read alongside the first page.
     *
     * Needed for `Group.mayRemove`, which decides every removal on this screen, and for the header's
     * member count. It is deliberately not inferred from the member list: members are paged
     * oldest-first, so an admin who joined late is on page four, and a screen that read its own
     * permissions off page one would show a moderator no controls until they scrolled. It is also
     * not passed in as a navigation argument, where it would go stale the moment somebody was
     * promoted.
     */
    val group: Group? = null,
    val members: List<GroupMember> = emptyList(),
    val query: String = "",
    val loadedOnce: Boolean = false,
    val loadingMore: Boolean = false,
    val hasMore: Boolean = false,
    /** A removal is in flight. Disables the action rather than letting a second tap through. */
    val busy: Boolean = false,
    val error: String? = null,
) {
    val visible: List<GroupMember> get() = filterMembers(members, query)

    /**
     * A search is running against an incomplete list. The screen says so rather than presenting a
     * partial answer as a complete one.
     */
    val searchIncomplete: Boolean get() = query.isNotBlank() && hasMore

    /** False until the group is loaded, which grants nothing. */
    fun mayRemove(member: GroupMember, me: UserId): Boolean = group?.mayRemove(member, me) == true

    /** False until the group is loaded. The same rule as [mayRemove], asked of the domain. */
    fun mayBan(member: GroupMember, me: UserId): Boolean = group?.mayBan(member, me) == true

    /** False until the group is loaded. Owner only, never the owner as a target, never yourself. */
    fun mayChangeRole(member: GroupMember, me: UserId): Boolean =
        group?.mayChangeRole(member, me) == true

    /**
     * Which way the single role control points for [member], or null when there is no such control.
     *
     * Null both when the viewer may not change roles at all and when the target's role is one
     * `updateGroupMemberRole` cannot assign, so a caller has exactly one thing to test.
     */
    fun roleChangeFor(member: GroupMember, me: UserId): GroupRoleAssignment? =
        if (mayChangeRole(member, me)) group?.nextRoleFor(member) else null

    /** Ownership may move only from the current owner to another live member. */
    fun mayTransferOwnership(member: GroupMember, me: UserId): Boolean = group?.let {
        it.permissions.canTransferOwnership && member.user.id != me && member.role != GroupRole.OWNER
    } == true
}

/**
 * Follows `BlockedUsersViewModel`: a cursor-paginated list, `hasMore` and a cursor both required
 * before a page is fetched, and the cursor reset on every refresh because one from a previous page
 * set points into a list that no longer exists.
 */
class GroupMembersViewModel(
    private val groupId: GroupId,
    private val groups: GroupRepository,
    /** The signed-in user, so their own row is never offered a removal. */
    private val me: UserId,
) : ViewModel() {

    private val _state = MutableStateFlow(GroupMembersUiState())
    val state: StateFlow<GroupMembersUiState> = _state.asStateFlow()

    /** Opaque and server-owned. Never parsed, compared or persisted. */
    private var cursor: String? = null

    /** A page request is in flight. Guards the paging footer, the search sweep, and each other. */
    private var paging = false

    private var loading = false

    init {
        refresh()
    }

    /** The Retry entry point: a fresh read that also takes the banner down. */
    fun refresh() = reload(clearError = true)

    /**
     * [clearError] exists because a moderation failure needs BOTH: the banner explaining what the
     * server refused, and a re-read of the group, since the roles this screen was drawn from are
     * evidently not the roles the server holds. Clearing the error on the way in would erase the
     * sentence the user is being shown, and the failure would look like a tap that did nothing.
     */
    private fun reload(clearError: Boolean) {
        if (loading) return
        loading = true
        if (clearError) _state.value = _state.value.copy(error = null)
        viewModelScope.launch {
            try {
                // The group first: without it the list cannot decide what any row may do, and
                // rendering rows before that is known would flash controls in.
                val group = groups.group(groupId)
                val page = groups.members(groupId)
                cursor = page.nextCursor
                _state.value = _state.value.copy(
                    group = group,
                    members = page.items,
                    hasMore = page.hasMore,
                    loadedOnce = true,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    loadedOnce = true,
                    // `listGroupMembers` is members-only even for a public group, and the server
                    // answers one code for "no such group" and "you are not in it". The sentence
                    // `groupBanner` gives it does not distinguish them either.
                    error = error.groupBanner("Could not load the members."),
                )
            } finally {
                loading = false
                // A query typed during the first load found no cursor to sweep from; start now.
                if (_state.value.query.isNotBlank()) loadAllRemaining()
            }
        }
    }

    /** The list's own paging, driven by the footer scrolling into view. */
    fun loadMore() {
        val current = _state.value
        if (paging || !current.hasMore) return
        val next = cursor ?: return
        paging = true
        _state.value = current.copy(loadingMore = true)
        viewModelScope.launch {
            try {
                fetchPage(next)
            } finally {
                paging = false
                _state.value = _state.value.copy(loadingMore = false)
            }
        }
    }

    /**
     * SPS §12.6's search.
     *
     * Typing pulls the REST of the list before the filter can be trusted -- see
     * [matchesMemberQuery] for why the filter is local at all. Clearing the box undoes nothing: the
     * pages fetched are still the group's members, and the list simply stops filtering.
     */
    fun onQueryChange(raw: String) {
        _state.value = _state.value.copy(query = raw)
        if (raw.isNotBlank()) loadAllRemaining()
    }

    private fun loadAllRemaining() {
        if (paging || !_state.value.hasMore) return
        paging = true
        _state.value = _state.value.copy(loadingMore = true)
        viewModelScope.launch {
            try {
                // Stops on the first failure rather than hammering a server that just refused. The
                // rows already fetched stay, the note under the search bar says the answer is
                // incomplete, and the banner's Retry is the way back.
                while (_state.value.hasMore) {
                    val next = cursor ?: break
                    if (!fetchPage(next)) break
                }
            } finally {
                paging = false
                _state.value = _state.value.copy(loadingMore = false)
            }
        }
    }

    /** True when the page landed. The caller owns [paging] and the `loadingMore` flag. */
    private suspend fun fetchPage(from: String): Boolean = try {
        val page = groups.members(groupId, cursor = from)
        cursor = page.nextCursor
        val held = _state.value.members
        // De-duplicated by user id, and this is a crash guard rather than tidiness: the list is
        // keyed by that id and `LazyColumn` throws on two items with the same key. The server's
        // keyset cursor does not repeat a row on its own, but a [refresh] landing while a page is
        // in flight replaces the list under this append, and the overlap would take the screen
        // down rather than showing somebody twice.
        val known = held.mapTo(mutableSetOf()) { it.user.id }
        _state.value = _state.value.copy(
            members = held + page.items.filterNot { it.user.id in known },
            hasMore = page.hasMore,
        )
        true
    } catch (cancellation: CancellationException) {
        throw cancellation
    } catch (error: Throwable) {
        // `hasMore` is left alone, so the end of the list can be tried again.
        _state.value = _state.value.copy(error = error.groupBanner("Could not load more members."))
        false
    }

    /**
     * `removeGroupMember` with `action=remove`.
     *
     * NEVER [GroupMemberRemoval.REMOVE_AND_BAN], and it is passed explicitly rather than left to the
     * repository's default so that is visible at the call site. The other value also bans, and a ban
     * is a state this build can neither show nor undo -- `listGroupBans` and `unbanGroupMember` are
     * a later pass. An action whose effect the app cannot display or reverse does not belong behind
     * a button here.
     *
     * NOT optimistic, unlike `BlockedUsersViewModel.unblock`. Removing somebody from a group is a
     * moderation act on a shared surface: showing them gone before the server agreed would let two
     * admins each believe they had removed a person who is still in the chat. Unblocking is private
     * to the person doing it, which is what made the optimistic direction safe there.
     *
     * The permission is re-checked here even though the row would not have offered the action --
     * roles change between a read and a tap, and this is the guard that does not depend on what was
     * drawn.
     */
    fun remove(userId: UserId) {
        val current = _state.value
        if (current.busy) return
        val target = current.members.firstOrNull { it.user.id == userId } ?: return
        if (!current.mayRemove(target, me)) return

        _state.value = current.copy(busy = true, error = null)
        viewModelScope.launch {
            try {
                groups.removeMember(groupId, userId, action = GroupMemberRemoval.REMOVE)
                dropMember(userId)
                refreshGroup()
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                when (error.asGroupFailure()) {
                    // The server answers this when the target is not a member. They are already
                    // gone, which is what was asked for -- reporting a failure would leave a row
                    // nobody can remove because the removal has already happened.
                    GroupFailure.UNAVAILABLE -> {
                        dropMember(userId)
                        refreshGroup()
                    }
                    GroupFailure.INSUFFICIENT_ROLE -> _state.value = _state.value.copy(
                        busy = false,
                        error = "You do not have permission to remove ${target.user.displayName}.",
                    )
                    else -> _state.value = _state.value.copy(
                        busy = false,
                        error = error.groupBanner("Could not remove that member."),
                    )
                }
            }
        }
    }

    /**
     * `banGroupMember`. `PUT /groups/{id}/bans/{user_id}`, 204.
     *
     * A ban REMOVES as well as bans, so the row leaves this list exactly as a removal does -- but
     * only after the server says so, never before. The person then appears on the group's ban list,
     * which is the only screen that can undo this.
     *
     * The same permission as removal, asked of the domain rather than restated, and re-checked here
     * for the same reason [remove] re-checks: seniority is read from a response that may be a
     * minute old.
     *
     * `403 not_conversation_member` is treated as success, like [remove]'s 404: the person is not in
     * the group, which is most of what was asked for. The list drops them so the row cannot be
     * tapped again -- leaving it would offer an action that can only fail the same way.
     */
    fun ban(userId: UserId) {
        val current = _state.value
        if (current.busy) return
        val target = current.members.firstOrNull { it.user.id == userId } ?: return
        if (!current.mayBan(target, me)) return

        _state.value = current.copy(busy = true, error = null)
        viewModelScope.launch {
            try {
                groups.banMember(groupId, userId)
                dropMember(userId)
                refreshGroup()
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                when (error.asGroupFailure()) {
                    GroupFailure.NOT_A_MEMBER, GroupFailure.UNAVAILABLE -> {
                        dropMember(userId)
                        refreshGroup()
                    }
                    GroupFailure.INSUFFICIENT_ROLE -> _state.value = _state.value.copy(
                        busy = false,
                        error = "You do not have permission to ban ${target.user.displayName}.",
                    )
                    else -> _state.value = _state.value.copy(
                        busy = false,
                        error = error.groupBanner("Could not ban that member."),
                    )
                }
            }
        }
    }

    /**
     * `updateGroupMemberRole`. 204. Owner only, both directions.
     *
     * [role] is passed in rather than derived here so the ViewModel performs the change the SCREEN
     * showed -- and then checks that it is still the change the group would allow. If the two have
     * diverged, because somebody was promoted while the card was open, the request is dropped and
     * the list is refreshed rather than sending a write whose meaning has changed.
     *
     * The row is not edited in place afterwards. The whole page is re-read, because a role change is
     * a fact about the group that other rows depend on: promoting somebody changes what THEY may do
     * to every other row, and `Group.role` for the viewer can change underneath it too.
     *
     * `409 ownership_transfer_required` is the owner as a target. The screen should not have offered
     * it -- `mayChangeRole` refuses the owner -- so when it happens the role this screen was drawn
     * from is stale, and the honest response is to say so and re-read.
     */
    fun changeRole(userId: UserId, role: GroupRoleAssignment) {
        val current = _state.value
        if (current.busy) return
        val target = current.members.firstOrNull { it.user.id == userId } ?: return
        // Both halves: may this viewer change this person's role at all, and is the direction the
        // screen asked for still the direction the group allows?
        if (current.roleChangeFor(target, me) != role) return

        _state.value = current.copy(busy = true, error = null)
        viewModelScope.launch {
            try {
                groups.updateMemberRole(groupId, userId, role)
                _state.value = _state.value.copy(busy = false)
                // Not patched in place -- see the note above.
                refresh()
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    busy = false,
                    error = when (error.asGroupFailure()) {
                        GroupFailure.INSUFFICIENT_ROLE ->
                            "Only the group's owner can change roles."
                        GroupFailure.OWNERSHIP_TRANSFER_REQUIRED ->
                            "The owner's role cannot be changed here. Transfer ownership instead."
                        GroupFailure.NOT_A_MEMBER, GroupFailure.UNAVAILABLE ->
                            "${target.user.displayName} is no longer in this group."
                        else -> error.groupBanner("Could not change that member's role.")
                    },
                )
                // Whatever the group looks like now, it is not what this screen was drawn from --
                // and the banner above stays up while it re-reads.
                reload(clearError = false)
            }
        }
    }

    fun transferOwnership(userId: UserId) {
        val current = _state.value
        if (current.busy) return
        val target = current.members.firstOrNull { it.user.id == userId } ?: return
        if (!current.mayTransferOwnership(target, me)) return

        _state.value = current.copy(busy = true, error = null)
        viewModelScope.launch {
            try {
                groups.transferOwnership(groupId, userId)
                _state.value = _state.value.copy(busy = false)
                refresh()
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    busy = false,
                    error = error.groupBanner("Could not transfer ownership."),
                )
                reload(clearError = false)
            }
        }
    }

    private fun dropMember(userId: UserId) {
        _state.value = _state.value.copy(
            busy = false,
            members = _state.value.members.filterNot { it.user.id == userId },
        )
    }

    /**
     * Re-reads the GROUP after a removal, for its `member_count`.
     *
     * Decrementing the held count locally would be inventing a number: somebody may have joined in
     * the meantime, and the count is what this screen's header and the group screen's Members row
     * both show. A failure here is swallowed -- the removal succeeded, and a banner about the count
     * would report the wrong thing as broken.
     */
    private fun refreshGroup() {
        viewModelScope.launch {
            runCatching { groups.group(groupId) }
                .onSuccess { _state.value = _state.value.copy(group = it) }
        }
    }

    fun dismissError() {
        _state.value = _state.value.copy(error = null)
    }
}
